import {Card, Trend} from 'platform/components';
import {Align, Box, Heading, Hide, Space, Text, ThemeIconKey, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {Trans} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../../../components/Alert/Alert';
import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import i18n from '../../../../i18n/i18n';
import {MarketType} from '../../../../types/MarketType';
import {VehicleType} from '../../../../types/VehicleType';
import {ResponsiveGrid} from '../../components/ResponsiveGrid';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {getDiffLabel} from '../../utils/getDiffLabel';
import {getValueOrEmptyString} from '../../utils/getValueOrEmptyString';
import {MileageChart} from './components/MileageChart';
import {getStatisticVariant} from './utils/getStatisticVariant';

interface StatisticsCardMileageProps extends TestIdProps {
  market?: MarketType;
  vehicle: VehicleType;
  country: string;
}

export function StatisticsCardMileage(props: StatisticsCardMileageProps) {
  const {isPrint} = useIsPrintForced();
  const formatNumber = useFormatNumber();
  const variant = getStatisticVariant(
    props.vehicle.mileage,
    props.market?.sold?.averageMileage?.value ?? 0
  );

  const alertTranslationKey = match(variant)
    .with('success', () => 'entity.overviewStatistics.labels.mileageAlertSuccess')
    .with('neutral', () => 'entity.overviewStatistics.labels.mileageAlertNeutral')
    .with('warning', () => 'entity.overviewStatistics.labels.mileageAlertWarning')
    .exhaustive();

  const alertIcon = match<string | undefined, ThemeIconKey | undefined>(variant)
    .with('success', () => 'action/trending_up')
    .with('warning', () => 'action/trending_down')
    .otherwise(() => undefined);

  const sold = props.market?.sold?.averageMileage?.value;
  const stock = props.market?.stock?.averageMileage?.value;

  const isSomeVehicleSold = (props.market?.sold?.similarVehicle ?? 0) > 0;
  const isSomeVehicleInStock = (props.market?.stock?.similarVehicle ?? 0) > 0;

  const averageMileageSold = isSomeVehicleSold ? props.market?.sold?.averageMileage : undefined;
  const averageMileageStock = isSomeVehicleInStock
    ? props.market?.stock?.averageMileage
    : undefined;

  const soldDifference =
    isNil(sold) || !isSomeVehicleSold ? undefined : props.vehicle.mileage - sold;
  const stockDifference =
    isNil(stock) || !isSomeVehicleInStock ? undefined : props.vehicle.mileage - stock;

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('mileageTitle', props)}>
                {i18n.t('entity.overviewStatistics.labels.mileageTitle')}
              </Heading>
            </Align>
            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('mileageDescription', props)}
            >
              <Trans
                i18nKey="entity.overviewStatistics.labels.mileageDescription"
                components={{bold: <Text alternative inline size="small" />}}
              />
            </Text>
            <ResponsiveGrid>
              <Box>
                <Align center>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('mileageYourCar', props)}
                  >
                    {i18n.t('entity.overviewStatistics.labels.mileageYourCar')}
                  </Text>
                  <Space vertical={2} />
                  <Heading size={2} data-testid={suffixTestId('vehicleMileage', props)}>
                    {`${formatNumber(props.vehicle.mileage, 0)} ${i18n.t('general.metric.km')}`}
                  </Heading>
                </Align>
              </Box>
              <Box>
                <VStack spacing={2} align="center">
                  <Align center>
                    <Text
                      size="xSmall"
                      color="secondary"
                      data-testid={suffixTestId('mileageSoldCars', props)}
                    >
                      {i18n.t('entity.overviewStatistics.labels.mileageSoldCars')}
                    </Text>
                    <Space vertical={2} />
                    <Heading size={2} data-testid={suffixTestId('soldAverageMileage', props)}>
                      {getValueOrEmptyString(
                        averageMileageSold,
                        (averageMileage) =>
                          `${formatNumber(averageMileage.value, 0)} ${averageMileage.unit}`
                      )}
                    </Heading>
                  </Align>
                  {isNotNil(soldDifference) && (
                    <Trend
                      label={getDiffLabel(
                        soldDifference,
                        (value) => `${formatNumber(value)} ${i18n.t('general.metric.km')}`
                      )}
                      isSubtle
                      isSmall
                      isPositive={soldDifference < 0}
                      isNegative={soldDifference > 0}
                      isReverseIcon
                      data-testid={suffixTestId('soldDifferenceTrend', props)}
                    />
                  )}
                </VStack>
              </Box>
              <Box>
                <VStack align="center" spacing={2}>
                  <Align center>
                    <Text
                      size="xSmall"
                      color="secondary"
                      data-testid={suffixTestId('mileageStockCars', props)}
                    >
                      {i18n.t('entity.overviewStatistics.labels.mileageStockCars')}
                    </Text>
                    <Space vertical={2} />
                    <Heading size={2} data-testid={suffixTestId('stockAverageMileage', props)}>
                      {getValueOrEmptyString(
                        averageMileageStock,
                        (averageMileage) =>
                          `${formatNumber(averageMileage.value, 0)} ${averageMileage.unit}`
                      )}
                    </Heading>
                  </Align>
                  {isNotNil(stockDifference) && (
                    <Trend
                      label={getDiffLabel(
                        stockDifference,
                        (value) => `${formatNumber(value)} ${i18n.t('general.metric.km')}`
                      )}
                      isSubtle
                      isSmall
                      isPositive={stockDifference < 0}
                      isNegative={stockDifference > 0}
                      isReverseIcon
                      data-testid={suffixTestId('stockDifferenceTrend', props)}
                    />
                  )}
                </VStack>
              </Box>
            </ResponsiveGrid>
            <Hide when={isNil(props.market?.sold?.averageMileage?.value) || !isSomeVehicleSold}>
              <Alert
                variant={variant === 'warning' ? 'warning' : 'success'}
                icon={alertIcon}
                data-testid={suffixTestId('mileageAlert', props)}
              >
                <Trans
                  i18nKey={alertTranslationKey}
                  values={{
                    mileage: getValueOrEmptyString(
                      soldDifference,
                      (difference) =>
                        `${formatNumber(Math.abs(difference), 0)} ${i18n.t('general.metric.km')}`
                    ),
                  }}
                  components={{bold: <Text alternative inline size="small" />}}
                />
              </Alert>
            </Hide>
            <MileageChart
              vehicle={props.vehicle}
              mileageChartData={props.market?.mileageChart}
              data-testid={suffixTestId('mileageChart', props)}
            />
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
